'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { LogOut, Menu, X } from 'lucide-react';
import { Logo } from './Logo';
import { InstallPrompt } from './InstallPrompt';
import { Button } from '@/components/ui/button';
import { NAV_LINKS } from '@/lib/constants';
import { cn } from '@/lib/utils';

function SignOutButton({
  className,
  size = 'sm',
}: {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'icon';
}) {
  return (
    <form action="/api/auth/signout" method="post" className={className}>
      <Button
        type="submit"
        variant="ghost"
        size={size}
        className="w-full text-cream/75 hover:text-gold-300"
        aria-label="Cerrar sesión"
      >
        <LogOut className="h-4 w-4" />
        <span className="ml-1">Cerrar sesión</span>
      </Button>
    </form>
  );
}

interface NavbarProps {
  authenticated?: boolean;
  variant?: 'public' | 'private';
  /** Enlace a /admin (solo rutas privadas con permiso). */
  isAdmin?: boolean;
}

export function Navbar({
  authenticated = false,
  variant = 'public',
  isAdmin = false,
}: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    const raf = window.requestAnimationFrame(handler);
    window.addEventListener('scroll', handler, { passive: true });
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', handler);
    };
  }, []);

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-all duration-500',
          scrolled
            ? 'border-b border-white/10 bg-navy-950/85 backdrop-blur-xl'
            : 'bg-transparent',
        )}
      >
        <nav className="container-page flex h-16 items-center justify-between gap-4 sm:h-18">
          <Logo />

          {variant === 'public' && (
            <ul className="hidden items-center gap-7 lg:flex">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <Link
                    href={l.href}
                    className="text-sm text-cream/75 transition-colors hover:text-gold-300"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <div className="hidden items-center gap-3 lg:flex">
            <InstallPrompt />
            {authenticated ? (
              <>
                {isAdmin && (
                  <Button asChild variant="outline" size="sm" className="border-gold-500/40 text-gold-200">
                    <Link href="/admin">Admin</Link>
                  </Button>
                )}
                <Button asChild variant="ghost" size="sm">
                  <Link href="/dashboard">Mi cuenta</Link>
                </Button>
                <Button asChild variant="primary" size="sm">
                  <Link href="/curso">Continuar curso</Link>
                </Button>
                <SignOutButton />
              </>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm">
                  <Link href="/login">Iniciar sesión</Link>
                </Button>
                <Button asChild variant="primary" size="sm">
                  <Link href="/registro">Empezar gratis</Link>
                </Button>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <InstallPrompt />
            <button
              type="button"
              className="-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-md text-cream/80 transition-colors hover:text-gold-300 active:bg-white/5"
              onClick={() => setMobileOpen((s) => !s)}
              aria-label="Abrir menú"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </nav>

        {mobileOpen && (
          <div className="border-t border-white/10 bg-navy-950/95 backdrop-blur-xl lg:hidden">
            <div className="container-page flex flex-col gap-1 py-4">
              {variant === 'public' &&
                NAV_LINKS.map((l) => (
                  <Link
                    key={l.href}
                    href={l.href}
                    onClick={() => setMobileOpen(false)}
                    className="rounded-lg px-3 py-3 text-base text-cream/80 transition-colors hover:bg-white/5 hover:text-gold-300"
                  >
                    {l.label}
                  </Link>
                ))}
              <div className="mt-2 flex flex-col gap-2 border-t border-white/10 pt-4">
                {authenticated ? (
                  <>
                    {isAdmin && (
                      <Button asChild variant="outline" className="w-full border-gold-500/40 text-gold-200">
                        <Link href="/admin" onClick={() => setMobileOpen(false)}>
                          Admin
                        </Link>
                      </Button>
                    )}
                    <Button asChild variant="outline" className="w-full">
                      <Link href="/dashboard" onClick={() => setMobileOpen(false)}>
                        Mi cuenta
                      </Link>
                    </Button>
                    <Button asChild variant="primary" className="w-full">
                      <Link href="/curso" onClick={() => setMobileOpen(false)}>
                        Continuar curso
                      </Link>
                    </Button>
                    <SignOutButton className="w-full" />
                  </>
                ) : (
                  <>
                    <Button asChild variant="outline" className="w-full">
                      <Link href="/login" onClick={() => setMobileOpen(false)}>
                        Iniciar sesión
                      </Link>
                    </Button>
                    <Button asChild variant="primary" className="w-full">
                      <Link
                        href="/registro"
                        onClick={() => setMobileOpen(false)}
                      >
                        Empezar gratis
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
      <div className="h-16 sm:h-18" aria-hidden />
    </>
  );
}
